import { Box, Button, Divider, Grid, Menu, MenuItem, Pagination, Typography } from '@mui/material';
import { BodyText, SubTitleText } from 'Application/components';
import { FC, useEffect, useState } from 'react';
import { LocalTravelDataTable } from './LocalTravelDataTable';
import { useAgencyStore } from 'Application/store';
import Loader from 'ui-component/Loader';
import AddSvData from 'Application/components/Modals/AddSvData';
import {
    addCommasToNumbers,
    changeDateFormat,
    concatenateLocalTravelDestinations,
    exportToCSV,
    getUserRoleNameFromStorage
} from 'Application/utils';
import { UserRole } from 'Application/models';
import { DownloadRounded, ExpandMoreRounded, FilterList } from '@mui/icons-material';
import { toast } from 'sonner';
import { LocalTravelFilters } from './LocalTravelFilters';

interface AgencyPerksProps {
    agencyName: string;
}

export const AgencyLocalTravel: FC<AgencyPerksProps> = ({ agencyName }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openAddSvDataForm, setOpenAddSvDataForm] = useState<boolean>(false);

    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(5);

    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const userRole = getUserRoleNameFromStorage();
    const isUserFromAgency =
        userRole === UserRole.ROLE_AGENCY_ADMIN || userRole === UserRole.ROLE_AGENT || userRole === UserRole.ROLE_BRANCH_MANAGER;

    const {
        isLoading: isGetAgencyPerksLoading,
        isError: isGetAgencyPerksError,
        error: getAgencyPerksError,
        agencyLocalTravelData: agencyLocalTravelData,
        filteredAgencyLocalTravelData: filteredAgencyLocalTravelData,
        getAgencyLocalTravelData: getAgencyLocalTravelData,
        getAgencyBranches: getAgencyBranches
    } = useAgencyStore();

    useEffect(() => {
        getAgencyLocalTravelData(agencyName, pageNumber, pageSize);
    }, [pageNumber, pageSize]);

    useEffect(() => {
        if (userRole !== UserRole.ROLE_AGENT && userRole !== UserRole.ROLE_BRANCH_MANAGER) {
            getAgencyBranches(agencyName);
        }
    }, []);

    const handleDataExport = () => {
        const localTravelData = !filteredAgencyLocalTravelData ? agencyLocalTravelData?.content : filteredAgencyLocalTravelData;

        if (!localTravelData || localTravelData.length === 0) {
            toast.error('No data available to export');
            return;
        }

        const exportContent = (localTravelData ?? []).map((data) => {
            const concatenatedDestinations = concatenateLocalTravelDestinations(data.destination);
            const formattedOriginValue = data.origin.split(',').length > 1 ? data.origin : data.origin.toTitle();

            return {
                Agent: data.agent,
                'No. of passengers': data.numOfPassengers,
                Branch: data.branch,
                'Total premium': addCommasToNumbers(data.totalPremium.toString()),
                'Balance notes': data.balanceNote,
                'SV number': data.svNumber,
                'Travelling from': formattedOriginValue,
                'Travelling to': concatenatedDestinations,
                'Departure date': changeDateFormat(data.departureDate),
                'Return date': changeDateFormat(data.returnDate),
                'Schedule status': data.scheduleStatus.toTitle(),
                'Created at': changeDateFormat(data.createdAt)
            };
        });

        exportToCSV('Local travel data', exportContent);
    };

    const handleRowsPerPageChange = (rows: number) => {
        setPageSize(rows);
        setPageNumber(1); // Reset to first page when rows per page change
        handleClose();
    };

    const agencyDataExist = agencyLocalTravelData !== null && agencyLocalTravelData.content.length > 0;

    return (
        <>
            <Box>
                <Box className="flex justify-between items-center mb-8">
                    <Box className="flex flex-col space-y-2">
                        <SubTitleText text={'Local Travel'} />
                        <BodyText text={'Explore the local travel data for your agency'} />
                    </Box>
                    <Box className="flex gap-4">
                        {!isGetAgencyPerksLoading && !isGetAgencyPerksError && agencyDataExist && (
                            <>
                                <Button onClick={handleDataExport} endIcon={<DownloadRounded />} disableElevation variant="contained">
                                    Export SV Data
                                </Button>
                                <Button variant="contained" startIcon={<FilterList />} onClick={() => setIsDrawerOpen(true)}>
                                    Filter
                                </Button>
                            </>
                        )}
                        {isUserFromAgency && (
                            <Button onClick={() => setOpenAddSvDataForm(true)} disableElevation variant="contained">
                                Add SV data
                            </Button>
                        )}
                    </Box>
                </Box>
                <LocalTravelFilters
                    isDrawerOpen={isDrawerOpen}
                    setOpenDrawer={setIsDrawerOpen}
                    agencyName={agencyName}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                />
                <Divider />
                {isGetAgencyPerksLoading || agencyLocalTravelData === null ? (
                    <Loader />
                ) : isGetAgencyPerksError ? (
                    <Typography color="error" className="mt-4">
                        {getAgencyPerksError}
                    </Typography>
                ) : (
                    <>
                        <LocalTravelDataTable
                            perksData={!filteredAgencyLocalTravelData ? agencyLocalTravelData.content : filteredAgencyLocalTravelData}
                            agencyName={agencyName}
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                        />
                        {!filteredAgencyLocalTravelData && agencyLocalTravelData.content.length > 0 && (
                            <Grid container justifyContent="space-between" alignItems="end" className="mt-4">
                                {agencyLocalTravelData.totalPages > 1 && (
                                    <Grid item>
                                        <Pagination
                                            className="mt-6"
                                            count={agencyLocalTravelData.totalPages}
                                            page={pageNumber}
                                            onChange={(_, value) => setPageNumber(value)}
                                            color="primary"
                                        />
                                    </Grid>
                                )}
                                <Grid item>
                                    <Button
                                        size="large"
                                        sx={{ color: 'grey.900' }}
                                        color="secondary"
                                        endIcon={<ExpandMoreRounded />}
                                        onClick={handleClick}
                                    >
                                        {pageSize} Rows
                                    </Button>
                                    {anchorEl && (
                                        <Menu
                                            id="menu-user-list-style1"
                                            anchorEl={anchorEl}
                                            keepMounted
                                            open={Boolean(anchorEl)}
                                            onClose={handleClose}
                                            variant="selectedMenu"
                                            anchorOrigin={{
                                                vertical: 'top',
                                                horizontal: 'right'
                                            }}
                                            transformOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'right'
                                            }}
                                        >
                                            {[5, 10, 20].map((rows) => (
                                                <MenuItem key={rows} onClick={() => handleRowsPerPageChange(rows)}>
                                                    {rows} Rows
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    )}
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}
            </Box>
            {openAddSvDataForm && isUserFromAgency && (
                <AddSvData
                    isOpen={openAddSvDataForm}
                    setIsOpen={setOpenAddSvDataForm}
                    agencyName={agencyName}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                />
            )}
        </>
    );
};
